/* eslint-disable react/prop-types -- HomeV2Presenter receives Omit<InvestmentCardV2Props,'onCtaClick'>[]; onCtaClick is added in presenter */
import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { RootState } from '../../store'
import { setPreviousUrl } from '../../lib/utils/helpers'
import {
  buildCampaignSignupRedirectFromOpportunity,
  storeCampaignSignupRedirect,
} from '../../lib/utils/campaign-signup-redirect'
import { showBanner } from '../../global-state/banner-slice'
import { comingSoonServices, projectServices } from '../../services'
import type { ComingSoonPublicItem } from '../../services/coming-soon-service'
import { EVENTS } from '../../lib/utils/constants'
import { captureEvent } from '../../lib/utils/posthogUtils/usePostHog'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'
import { HomeV2Presenter } from './home-v2-presenter'
import { opportunityToCardProps } from './format-opportunity'
import type { InvestmentCardV2Props } from './investment-card-v2'
import { RawOpportunity } from '../../lib/types'
import { shouldPromptConfidentialityAgreement } from '../../lib/utils/onboarding'

export const HomeV2Container: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { authToken, isEmailVerified, acceptedNDA, riskProfileCompleted } = useSelector(
    (state: RootState) => state.investor,
  )

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['opportunities', 'home-v2', 'featured'],
    queryFn: () =>
      projectServices.getFeaturedOpportunity({
        Authorization: authToken ?? '',
      }),
  })

  // The highlighted opportunity (metadata.highlight_enabled) is independent of is_featured, so it
  // is looked up from the published listing rather than the featured list above.
  const { data: highlightData } = useQuery({
    queryKey: ['opportunities', 'home-v2', 'highlighted'],
    queryFn: () =>
      projectServices.getSearchOpportunities(
        { limit: '50', offset: '0', sortBy: 'DISPLAY_ORDER', status: 'PUBLISHED' },
        { Authorization: authToken ?? '' },
      ),
  })

  const {
    data: comingSoonData,
    isLoading: comingSoonIsLoading,
    isFetching: comingSoonIsFetching,
  } = useQuery({
    queryKey: ['coming-soon-opportunities', 'public'],
    queryFn: async () => {
      try {
        return await comingSoonServices.getPublicList()
      } catch {
        return { items: [] as ComingSoonPublicItem[] }
      }
    },
  })

  const projects = (data?.opportunities ?? []).slice(0, 3)
  const hasLoggedVisitRef = useRef(false)

  useEffect(() => {
    if (hasLoggedVisitRef.current) return
    hasLoggedVisitRef.current = true
    logCrmEvent(EngagementEvents.VISITED_HOME_PAGE, { Authorization: authToken ?? '' })
    captureEvent(EVENTS.user_visited_credibila, {})
  }, [authToken])

  const handleProjectClick = (projectSlug: string, id: number, title?: string) => {
    if (authToken && isEmailVerified) {
      if (shouldPromptConfidentialityAgreement(riskProfileCompleted, acceptedNDA)) {
        setPreviousUrl(`/opportunity/${projectSlug}/${id}`)
      } else {
        setPreviousUrl('')
      }
      navigate(`/opportunity/${projectSlug}/${id}`)
    } else if (authToken && !isEmailVerified) {
      dispatch(
        showBanner({
          text: 'Please verify your email address to view the opportunity details.',
          variant: 'warning-banner',
        }),
      )
    } else {
      storeCampaignSignupRedirect(
        buildCampaignSignupRedirectFromOpportunity(projectSlug, id, title),
      )
      navigate('/login')
    }
  }

  const toCard = (opp: RawOpportunity): Omit<InvestmentCardV2Props, 'onCtaClick'> => {
    const props = opportunityToCardProps(
      opp,
      () => handleProjectClick(opp.opportunity_slug, opp.id, opp.opportunity_title),
      { enableHighlight: true },
    )
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- onCtaClick omitted; presenter injects it
    const { onCtaClick, ...rest } = props
    return rest
  }

  const highlightedOpportunity = (highlightData?.opportunities ?? []).find(
    (opp: RawOpportunity) => opp.metadata?.highlight_enabled === true,
  )
  const highlightedCard = highlightedOpportunity ? toCard(highlightedOpportunity) : undefined

  // The highlighted opportunity is always in the marketplace row, even when it is not featured.
  // The listing copy is used so the card carries the highlight flag.
  const marketplaceOpportunities = highlightedOpportunity
    ? [
        highlightedOpportunity,
        ...projects.filter((p: RawOpportunity) => p.id !== highlightedOpportunity.id),
      ].slice(0, 3)
    : projects
  const opportunityCards = marketplaceOpportunities.map(toCard)

  return (
    <HomeV2Presenter
      highlightedCard={highlightedCard}
      opportunityCards={opportunityCards}
      onOpportunityCtaClick={handleProjectClick}
      opportunityLoading={isLoading || isFetching}
      comingSoonItems={comingSoonData?.items ?? []}
      comingSoonLoading={comingSoonIsLoading || comingSoonIsFetching}
    />
  )
}
